import { Background } from '@unistocks/components/background/background';
import { Card } from '@unistocks/components/layout/card';
import {
  SIDEBAR_COLLAPSED_WIDTH,
  SIDEBAR_EXPANDED_WIDTH
} from '@unistocks/components/navigation/sidebar';
import { PageContext } from '@unistocks/components/page/context';
import { PageHeading } from '@unistocks/components/page/header/heading/page-heading';
import {
  PageScrollView,
  PageVirtualList
} from '@unistocks/components/page/scrollview/page-scroll-view';
import { QueryState, QueryStateProps } from '@unistocks/components/query-state/query-state';
import { breakpoints } from '@unistocks/shared/breakpoints';
import { useAppSelector } from '@unistocks/shared/hooks/redux-hooks';
import { selectSidebarPinned } from '@unistocks/shared/store/root.slice';
import { Fragment } from 'react';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { GetProps, Main, ViewProps, isWeb, styled, withStaticProperties } from 'tamagui';

export const PAGE_CONTENT_NAME = 'PageContent';
export const PAGE_CONTAINER_NAME = 'PageContainer';
export const PAGE_FRAME_NAME = 'PageFrame';

const PAGE_WIDTH_SMALL = 540;
const PAGE_WIDTH_MEDIUM = 840;

const getMarginLeftExpression = (pageSize: number, sidebarSize: number): ViewProps['maxW'] =>
  `calc((100% - ${pageSize}px) / 2 + ${sidebarSize}px / 2)`;

const PageFrameImpl = styled(Main, {
  name: PAGE_FRAME_NAME,
  context: PageContext,
  transition: '200ms',
  // animateOnly: ['max-width', 'margin'],
  overflow: 'visible',
  mx: 'auto',
  width: '100%',

  '$platform-native': {
    flex: 1
  },

  '$platform-web': {
    flexGrow: 1,
    // Same as header height $7
    mt: '$11'
  },

  variants: {
    sidebar: {
      true: {
        $md: {
          ml: `calc(${SIDEBAR_COLLAPSED_WIDTH}px + var(--t-space-5))`,
          maxW: `calc(100% - ${SIDEBAR_COLLAPSED_WIDTH}px - var(--t-space-5) * 2)`
        },

        $lg: {
          ml: `calc(${SIDEBAR_COLLAPSED_WIDTH}px + var(--t-space-5))`,
          maxW: `calc(100% - ${SIDEBAR_COLLAPSED_WIDTH}px - var(--t-space-5) * 2)`
        },

        $xl: {
          ml: `calc(${SIDEBAR_COLLAPSED_WIDTH}px + var(--t-space-5) + (100vw - ${SIDEBAR_COLLAPSED_WIDTH}px - var(--t-space-5) * 2 - calc(${breakpoints.xl}px - ${SIDEBAR_COLLAPSED_WIDTH}px - var(--t-space-5) * 2)) / 2)`,
          maxW: `calc(${breakpoints.xl}px - ${SIDEBAR_COLLAPSED_WIDTH}px - var(--t-space-5) * 2)`
        },

        $xxl: {
          ml: `calc(${SIDEBAR_COLLAPSED_WIDTH}px + var(--t-space-5) + (100vw - ${SIDEBAR_COLLAPSED_WIDTH}px - var(--t-space-5) * 2 - calc(${breakpoints.xxl}px - ${SIDEBAR_COLLAPSED_WIDTH}px - var(--t-space-5) * 2)) / 2)`,
          maxW: `calc(${breakpoints.xxl}px - ${SIDEBAR_COLLAPSED_WIDTH}px - var(--t-space-5) * 2)`
        },
        $xxxl: {
          ml: `calc(${SIDEBAR_COLLAPSED_WIDTH}px + var(--t-space-5) + (100vw - ${SIDEBAR_COLLAPSED_WIDTH}px - var(--t-space-5) * 2 - calc(${breakpoints.xxl}px - var(--t-space-5) * 2)) / 2)`,
          maxW: `calc(${breakpoints.xxl}px - var(--t-space-5) * 2)`
        }
      },

      pinned: {
        $md: {
          ml: `calc(${SIDEBAR_COLLAPSED_WIDTH}px + var(--t-space-5))`,
          maxW: `calc(100% - ${SIDEBAR_COLLAPSED_WIDTH}px - var(--t-space-5) * 2)`
        },

        $lg: {
          ml: `calc(${SIDEBAR_EXPANDED_WIDTH}px + var(--t-space-5))`,
          maxW: `calc(100% - ${SIDEBAR_EXPANDED_WIDTH}px - var(--t-space-5) * 2)`
        },

        $xl: {
          ml: `calc(${SIDEBAR_EXPANDED_WIDTH}px + var(--t-space-5) + (100vw - ${SIDEBAR_EXPANDED_WIDTH}px - var(--t-space-5) * 2 - calc(${breakpoints.xl}px - ${SIDEBAR_EXPANDED_WIDTH}px - var(--t-space-5) * 2)) / 2)`,
          maxW: `calc(${breakpoints.xl}px - ${SIDEBAR_EXPANDED_WIDTH}px - var(--t-space-5) * 2)`
        },

        $xxl: {
          ml: `calc(${SIDEBAR_EXPANDED_WIDTH}px + var(--t-space-5) + (100vw - ${SIDEBAR_EXPANDED_WIDTH}px - var(--t-space-5) * 2 - calc(${breakpoints.xxl}px - ${SIDEBAR_EXPANDED_WIDTH}px - var(--t-space-5) * 2)) / 2)`,
          maxW: `calc(${breakpoints.xxl}px - ${SIDEBAR_EXPANDED_WIDTH}px - var(--t-space-5) * 2)`
        },
        $xxxl: {
          ml: `calc(${SIDEBAR_EXPANDED_WIDTH}px + var(--t-space-5) + (100vw - ${SIDEBAR_EXPANDED_WIDTH}px - var(--t-space-5) * 2 - calc(${breakpoints.xxl}px - var(--t-space-5) * 2)) / 2)`,
          maxW: `calc(${breakpoints.xxl}px - var(--t-space-5) * 2)`
        }
      }
    },

    size: {
      small: {
        maxW: PAGE_WIDTH_SMALL,

        $md: {
          ml: getMarginLeftExpression(PAGE_WIDTH_SMALL, SIDEBAR_COLLAPSED_WIDTH)
        }
      },

      smallPinned: {
        maxW: PAGE_WIDTH_SMALL,

        $md: {
          ml: getMarginLeftExpression(PAGE_WIDTH_SMALL, SIDEBAR_COLLAPSED_WIDTH)
        },

        $lg: {
          ml: getMarginLeftExpression(PAGE_WIDTH_SMALL, SIDEBAR_EXPANDED_WIDTH)
        }
      },

      medium: {
        maxW: PAGE_WIDTH_SMALL,

        $md: {
          ml: getMarginLeftExpression(PAGE_WIDTH_SMALL, SIDEBAR_COLLAPSED_WIDTH)
        },

        $lg: {
          maxW: PAGE_WIDTH_MEDIUM,
          ml: getMarginLeftExpression(PAGE_WIDTH_MEDIUM, SIDEBAR_COLLAPSED_WIDTH)
        }
      },

      mediumPinned: {
        maxW: PAGE_WIDTH_SMALL,

        $md: {
          ml: getMarginLeftExpression(PAGE_WIDTH_SMALL, SIDEBAR_COLLAPSED_WIDTH)
        },

        $lg: {
          ml: getMarginLeftExpression(PAGE_WIDTH_SMALL, SIDEBAR_EXPANDED_WIDTH)
        },

        $xl: {
          maxW: PAGE_WIDTH_MEDIUM,
          ml: getMarginLeftExpression(PAGE_WIDTH_MEDIUM, SIDEBAR_EXPANDED_WIDTH)
        }
      },

      fullscreen: {
        maxW: '100%',

        $md: {
          maxW: `calc(100% - ${SIDEBAR_COLLAPSED_WIDTH}px - var(--t-space-3) * 2)`,
          ml: `calc(${SIDEBAR_COLLAPSED_WIDTH}px + var(--t-space-3))`
        }
      },

      fullscreenPinned: {
        maxW: '100%',

        $md: {
          maxW: `calc(100% - ${SIDEBAR_COLLAPSED_WIDTH}px - var(--t-space-3) * 2)`,
          ml: `calc(${SIDEBAR_COLLAPSED_WIDTH}px + var(--t-space-3))`
        },

        $lg: {
          maxW: `calc(100% - ${SIDEBAR_EXPANDED_WIDTH}px - var(--t-space-3) * 2)`,
          ml: `calc(${SIDEBAR_EXPANDED_WIDTH}px + var(--t-space-3))`
        }
      },

      // default / large
      default: {
        $md: {
          maxW: breakpoints.sm
        },

        $lg: {
          maxW: breakpoints.md + 100
        },

        $xl: {
          maxW: breakpoints.lg + 150
        },

        $xxl: {
          maxW: breakpoints.xl + 150
        },

        $xxxl: {
          maxW: breakpoints.xxl + 150
        }
      }
    }
  } as const,

  defaultVariants: {
    sidebar: false,
    size: 'default'
  }
});

type PageFrameImplProps = GetProps<typeof PageFrameImpl>;

export type PageProps = Omit<PageFrameImplProps, 'sidebar' | 'size'> & {
  queryState?: QueryStateProps;
} & {
  sidebar?: boolean;
  /**
   * Defines the size of the page content area.
   * @default 'large'
   */
  size?: 'small' | 'medium' | 'large' | 'fullscreen';
};

const PageFrame = PageFrameImpl.styleable<PageProps>(
  ({ children, sidebar: sidebarProp, size: sizeProp, queryState, ...rest }, forwardedRef) => {
    const { top } = useSafeAreaInsets();
    const pinnedSidebar = useAppSelector(selectSidebarPinned);

    const pageSidebar: PageFrameImplProps['sidebar'] = isWeb
      ? sidebarProp === false || !!sizeProp
        ? false
        : pinnedSidebar
          ? 'pinned'
          : true
      : false;

    const size: PageFrameImplProps['size'] = isWeb
      ? sizeProp === 'small'
        ? pinnedSidebar
          ? 'smallPinned'
          : 'small'
        : sizeProp === 'medium'
          ? pinnedSidebar
            ? 'mediumPinned'
            : 'medium'
          : sizeProp === 'fullscreen'
            ? pinnedSidebar
              ? 'fullscreenPinned'
              : 'fullscreen'
            : pageSidebar
              ? undefined
              : 'default'
      : 'default';

    /* Extra background is needed on native for page transitions. Because the screens have transparent backgrounds, they will overlap during animations */
    const Container = isWeb ? Fragment : Background;

    const wrappedChildren = queryState ? (
      <QueryState mt={'$10'} {...queryState}>
        {children}
      </QueryState>
    ) : (
      children
    );

    return (
      <Container>
        <PageFrameImpl
          ref={forwardedRef}
          transition={'200ms'}
          sidebar={pageSidebar}
          size={size}
          mt={top}
          {...rest}
        >
          {wrappedChildren}
        </PageFrameImpl>
      </Container>
    );
  }
);

const PageContent = styled(Card, {
  name: PAGE_CONTENT_NAME,
  padding: '$3',
  borderRadius: '$2',
  flexShrink: 1,
  transition: 'medium',
  animateOnly: ['opacity', 'transform'],
  enterStyle: { o: 0, y: -20 }
});

export const Page = withStaticProperties(PageFrame, {
  Heading: PageHeading,
  Content: withStaticProperties(PageContent, {
    Header: Card.Header,
    Heading: Card.Heading,
    Footer: Card.Footer
  }),
  ScrollView: PageScrollView,
  VirtualList: PageVirtualList
});
